// ── The /all-data-fields payload: shape + a one-shot handoff slot ───────────
// Dependency-free on purpose (no `./api` import), for the same reason
// readCache.js is: api.js and workflowApi.js both need this, and api.js
// importing workflowApi.js would close a cycle.

// Core keys stay at top level; everything else lives in `properties` JSONB.
// The send-side counterpart (packFieldProperties) stays in workflowApi.js —
// only the receive-side is shared.
/** After receiving from API: spread `properties` back as flat keys. */
export const unpackFieldProperties = (field) => {
  if (!field) return field;
  const { properties, columns, ...rest } = field;
  return {
    ...rest,
    ...(properties || {}),
    ...(columns ? { columns: columns.map(unpackFieldProperties) } : {}),
  };
};

/**
 * Normalize a raw all-data-fields payload — from the standalone endpoint OR
 * embedded in a view GET — into the shape every consumer expects. One
 * implementation so the two transports can never drift.
 */
export const normalizeAllDataFields = (raw) => {
  const wfRaw = raw?.workflow_data_fields || [];
  const workflowDataFields = Array.isArray(wfRaw) ? wfRaw.map(unpackFieldProperties) : [];

  const actRaw = raw?.activity_data_fields;
  const activityDataFields = Array.isArray(actRaw)
    ? actRaw.map((activity) => ({
        ...activity,
        data_fields: (activity.data_fields || []).map(unpackFieldProperties),
      }))
    : [];

  return { workflowDataFields, activityDataFields };
};

// ── One-shot handoff ────────────────────────────────────────────────────────
// A view GET with ?include=fields carries its version's catalog. The view
// object itself is dispatched into TWO Redux places (the selected item and the
// list entry), so the catalog must not ride along inside it — it is parked
// here instead, and useWorkflowFields collects it.
//
// Deliberately consume-once: a parked payload can only ever serve the load it
// arrived with, so it can never go stale. Re-entering a view refetches the
// view (useEditorRoute refetches on every paramId change) and re-parks.
const _primed = new Map(); // key -> { raw, keys }

/**
 * Park a raw payload under one or more version keys. Aliases exist because
 * RV/DV/AV routes identify a version by its integer id while the chart
 * template route uses its uuid — either may be the key that asks for it.
 */
export const primeAllDataFields = (keys, raw) => {
  if (!raw) return;
  const list = (Array.isArray(keys) ? keys : [keys])
    .filter((k) => k !== null && k !== undefined && k !== '')
    .map(String);
  if (list.length === 0) return;
  const entry = { raw, keys: list };
  list.forEach((k) => _primed.set(k, entry));
};

/** Collect a parked payload, clearing it and every alias of it. */
export const takePrimedAllDataFields = (key) => {
  if (key === null || key === undefined) return null;
  const entry = _primed.get(String(key));
  if (!entry) return null;
  entry.keys.forEach((k) => _primed.delete(k));
  return entry.raw;
};

/**
 * Take the embedded catalog off a view response, park it, and hand back the
 * view without it. A response that carries no catalog (older backend, RDBMS
 * view, or a caller that never asked) is returned untouched — the fetch path
 * in useWorkflowFields then behaves exactly as it did before.
 */
export const absorbAllDataFields = (view) => {
  if (!view || typeof view !== 'object') return view;
  const raw = view.all_data_fields;
  if (!raw) return view;
  primeAllDataFields([view.workflow_version_id, view.workflow_version_uid], raw);
  const { all_data_fields: _omit, ...rest } = view;
  return rest;
};
